"use client"
import {
    selectSize,
    setAvailableSizes,
    updateCanAddToCart,
    useAppDispatch,
    useAppSelector
} from "@/app/(site)/contexts/Global"
import React, { useEffect } from "react"
import ButtonPill from "../../../Molecules/ButtonPill"

type SizeChartProps = {
    sizes: Size[]
    setErrorStyles: React.Dispatch<React.SetStateAction<boolean>>
    errorStyles: boolean
    initialSizeValue?: string
}

export type Size = {
    sizeId: string
    item: string
    name: string
    sku: string
    isAvailable: boolean
    isSelected: boolean
}

// Helper function for deep comparison
function areSizesDifferent(sizes1: Size[], sizes2: Size[]): boolean {
    if (sizes1.length !== sizes2.length) return true
    return sizes1.some((size, index) => {
        const size2 = sizes2[index]
        return size.sizeId !== size2.sizeId || size.isSelected !== size2.isSelected
    })
}

export default function SizeChart({
    sizes,
    errorStyles,
    initialSizeValue
}: SizeChartProps) {
    const dispatch = useAppDispatch()
    const availableSizesContext = useAppSelector(state => state.product.availableSizes)

    useEffect(() => {
        const mergedSizes = sizes.map(size => {
            const existingSize = availableSizesContext.find(s => s.sizeId === size.sizeId)
            return {
                ...size,
                isSelected: existingSize ? existingSize.isSelected : false
            }
        })

        if (areSizesDifferent(mergedSizes, availableSizesContext)) {
            dispatch(setAvailableSizes(mergedSizes))
        }
    }, [sizes, availableSizesContext, dispatch])

    const handleSelectSize = (selectedSize: Size, initialSelectedSize?: Size[]) => {
        const updatedSizes = availableSizesContext.map(s => {
            const isAvailable = initialSelectedSize
                ? initialSelectedSize.some(
                      initialSize =>
                          initialSize.sizeId === s.sizeId && initialSize.isAvailable
                  )
                : s.isAvailable

            return {
                ...s,
                isSelected: s.sizeId === selectedSize.sizeId,
                isAvailable
            }
        })

        dispatch(setAvailableSizes(updatedSizes))
        dispatch(selectSize(selectedSize.name))
        dispatch(updateCanAddToCart())
    }

    return (
        <div className="flex gap-16">
            <div className="flex flex-wrap gap-8 md:gap-16">
                {Array.isArray(availableSizesContext) && availableSizesContext.length > 0
                    ? availableSizesContext.map((size, index) => (
                          <ButtonPill
                              key={index}
                              color="black"
                              variant="secondary"
                              size="small"
                              className={`border-[#E8E8E8] !px-16 !py-12 ${
                                  size.isAvailable ? "" : "!bg-[#F4F5F5] line-through"
                              } ${
                                  size.isSelected
                                      ? "!border !border-main-black"
                                      : "!border !border-main-medium-grey"
                              } ${errorStyles ? "!border-red-500" : ""}`}
                              onClick={() => handleSelectSize(size, sizes)}
                              aria-label={size.name}
                              type="button"
                              name="size"
                          >
                              {size.name}
                          </ButtonPill>
                      ))
                    : sizes.map((size, index) => (
                          <ButtonPill
                              key={index}
                              color="black"
                              variant="secondary"
                              size="small"
                              className={`border-[#E8E8E8] !px-16 !py-12 ${
                                  size.isAvailable ? "" : "!bg-[#F4F5F5] line-through"
                              } ${
                                  size.name === initialSizeValue
                                      ? "!border !border-main-black"
                                      : "!border !border-main-medium-grey"
                              } ${errorStyles ? "!border-red-500" : ""}`}
                              onClick={() => handleSelectSize(size, sizes)}
                              aria-label={size.name}
                              type="button"
                              name="size"
                          >
                              {size.name}
                          </ButtonPill>
                      ))}
            </div>
        </div>
    )
}
